<script setup lang="ts">
import { Head, Link, router, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Tag from '@/Components/Utils/Tag.vue'
import AddBrandDesignModal from '@/Pages/Kasir/Brands/AddBrandDesignModal.vue'
import BrandExclusiveTag from '@/Pages/Kasir/Brands/BrandExclusiveTag.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxes, faExternalLink, faPrint, faUndoAlt } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faBoxes, faExternalLink, faPrint, faUndoAlt)

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    data: {
        id: number
        slug: string
        name: string
        phone?: string
        recipient_phone?: string
        status?: string
        status_label?: string
        description?: string
        address?: string
        special_note?: string
        customer?: { name: string, slug: string } | null
    }
    products: any[]
    designs: any[]
    recent_orders: any[]
    stats: Record<string, { label: string, value: number }>
}>()

const isModalAddDesignOpen = ref(false)

const page = usePage()
const stockError = computed(() => (page.props.errors as Record<string, string>)?.stock)

const enablingStockId = ref<number | null>(null)
const enableStock = (brandProductId: number) => {
    enablingStockId.value = brandProductId
    router.post(route('kasir.stocks.store'), { brand_product_id: brandProductId }, {
        onFinish: () => enablingStockId.value = null,
    })
}

const removingId = ref<number | null>(null)
const removeProduct = (brandProductId: number) => {
    removingId.value = brandProductId
    router.delete(route('kasir.brand-products.delete', brandProductId), {
        preserveScroll: true,
        onFinish: () => removingId.value = null,
    })
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #otherBefore>
            <a :href="route('kasir.brands.shipping-label', data.slug)" target="_blank" rel="noopener">
                <Button v-tooltip="'Cetak 3 resi pengiriman di kertas A4'" style="tertiary" label="Cetak Resi" icon="fal fa-print" />
            </a>
            <a :href="route('kasir.brands.shipping-label', data.slug) + '?paper=f4'" target="_blank" rel="noopener">
                <Button v-tooltip="'Cetak 4 resi pengiriman di kertas F4'" style="tertiary" label="Cetak Resi F4" icon="fal fa-print" />
            </a>
        </template>
    </PageHeading>

    <div class="grid gap-4 px-4 py-4 lg:grid-cols-3">
        <div class="h-fit overflow-hidden rounded-xl border border-sky-400">
            <div class="border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="flex items-center gap-x-2 font-bold">
                    {{ data.name }}
                    <BrandExclusiveTag v-if="data.status === 'eksklusif'" />
                    <Tag v-else-if="data.status_label" :label="data.status_label" :theme="8" noHoverColor />
                </div>
                <div class="text-sm/6 text-gray-500">{{ data.description || 'Tanpa deskripsi' }}</div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Pelanggan</dt>
                    <dd class="text-right font-medium">
                        <Link v-if="data.customer" :href="route('kasir.customers.show', data.customer.slug)" class="secondaryLink">
                            {{ data.customer.name }}
                        </Link>
                        <span v-else class="italic text-gray-400">-</span>
                    </dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">No. telp</dt>
                    <dd class="text-right font-medium">{{ data.phone || '-' }}</dd>
                </div>
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">No. penerima</dt>
                    <dd class="text-right font-medium">
                        {{ data.recipient_phone || data.phone || '-' }}
                        <span v-if="!data.recipient_phone && data.phone" class="block text-xs font-normal text-gray-400">Sama dengan no. telp brand</span>
                    </dd>
                </div>
                <div v-if="data.address" class="py-3">
                    <dt class="mb-1 text-gray-500">Alamat</dt>
                    <dd class="text-gray-700">{{ data.address }}</dd>
                </div>
                <div v-if="data.special_note" class="py-3">
                    <dt class="mb-1 text-gray-500">Catatan khusus</dt>
                    <dd class="rounded-md bg-amber-50 p-2 text-amber-800">{{ data.special_note }}</dd>
                </div>
            </dl>

            <div class="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 text-center">
                <div v-for="(stat, key) in stats" :key="key" class="px-2 py-3">
                    <div class="text-lg font-semibold">{{ useFormatNumber(stat.value) }}</div>
                    <div class="text-xs text-gray-400">{{ stat.label }}</div>
                </div>
            </div>
        </div>

        <div class="lg:col-span-2 flex flex-col gap-4">
            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="flex items-center justify-between gap-x-3 border-b border-gray-900/5 bg-gray-50 px-4 py-3 sm:px-6">
                    <span class="font-semibold text-gray-700">Produk brand</span>
                    <Link :href="route('kasir.brands.products.create', data.id)">
                        <Button
                            size="xs"
                            label="Tambah produk"
                            icon="fal fa-plus"
                        />
                    </Link>
                </div>

                <div v-if="stockError" class="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-600 sm:px-6">
                    {{ stockError }}
                </div>

                <div v-if="!products.length" class="px-6 py-6 text-center text-sm text-gray-400">
                    Belum ada produk. Tambahkan dari katalog.
                </div>

                <ul v-else class="divide-y divide-gray-100 text-sm">
                    <li v-for="brandProduct in products" :key="brandProduct.id" class="flex items-start gap-x-3 px-4 py-3 sm:px-6">
                        <div class="min-w-0 flex-1">
                            <Link
                                v-if="brandProduct.cs_product_slug"
                                :href="route('kasir.products.show', brandProduct.cs_product_slug)"
                                class="font-medium primaryLink"
                            >
                                {{ brandProduct.display_name }}
                            </Link>
                            <div v-else class="font-medium">{{ brandProduct.display_name }}</div>
                            <div class="truncate font-mono text-xs text-gray-400">{{ brandProduct.sku }}</div>
                            <div class="truncate text-xs text-gray-500">
                                {{ brandProduct.category || 'Tanpa kategori' }} &middot; {{ brandProduct.print_type_label }}
                            </div>
                            <div v-if="brandProduct.note" class="text-xs text-gray-500">{{ brandProduct.note }}</div>
                            <div class="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                                <template v-if="brandProduct.stock">
                                    <Link
                                        :href="route('kasir.stocks.show', brandProduct.stock.id)"
                                        class="inline-flex items-center gap-x-1 secondaryLink"
                                    >
                                        <FontAwesomeIcon icon="fal fa-boxes" fixed-width aria-hidden="true" />
                                        {{ brandProduct.is_general ? 'Stok umum' : 'Sistem stok' }}:
                                        <span :class="brandProduct.stock.available > 0 ? 'font-medium' : 'font-medium text-red-500'">
                                            {{ useFormatNumber(brandProduct.stock.available) }} pcs tersedia
                                        </span>
                                        <span v-if="brandProduct.stock.available !== brandProduct.stock.on_hand" class="text-gray-400">
                                            (fisik {{ useFormatNumber(brandProduct.stock.on_hand) }})
                                        </span>
                                    </Link>
                                    <ModalConfirmationDelete
                                        v-if="!brandProduct.is_general"
                                        title="Kembali ke sistem pesanan?"
                                        :description="brandProduct.stock.on_hand > 0
                                            ? `Produk ini akan diproduksi setiap ada pesanan. Sisa stok ${useFormatNumber(brandProduct.stock.on_hand)} pcs akan dianggap HABIS (tercatat di riwayat stok).`
                                            : 'Produk ini akan diproduksi setiap ada pesanan.'"
                                        yesLabel="Ya, kembali ke sistem pesanan"
                                        yesIcon="fal fa-undo-alt"
                                        :routeDelete="{ name: 'kasir.stocks.archive', parameters: [brandProduct.stock.id], method: 'patch' }"
                                    >
                                        <template #default="{ changeModel }">
                                            <Button size="xxs" type="tertiary" icon="fal fa-undo-alt" label="Kembali ke sistem pesanan" @click="() => changeModel()" />
                                        </template>
                                    </ModalConfirmationDelete>
                                </template>
                                <template v-else-if="!brandProduct.is_general">
                                    <span class="text-gray-400">Diproduksi per pesanan</span>
                                    <Button
                                        size="xxs"
                                        type="secondary"
                                        icon="fal fa-boxes"
                                        label="Pakai sistem stok"
                                        :loading="enablingStockId === brandProduct.id"
                                        v-tooltip="'Klik untuk mengubah produk ini menjadi sistem stok brand, bukan dibuat per pesanan'"
                                        @click="enableStock(brandProduct.id)"
                                    />
                                </template>
                            </div>
                        </div>

                        <div class="shrink-0 text-right">
                            <div class="font-medium">{{ useFormatCurrency(brandProduct.effective_price) }}</div>
                            <div class="text-xs text-gray-400">{{ brandProduct.has_custom_price ? 'harga brand' : 'harga katalog' }}</div>
                            <div v-if="brandProduct.price_tiers.length > 1" class="mt-1 flex flex-col text-xs text-gray-500">
                                <span v-for="tier in brandProduct.price_tiers.slice(1)" :key="tier.min_quantity">
                                    &ge; {{ tier.min_quantity }} {{ useFormatCurrency(tier.price) }}
                                </span>
                            </div>
                        </div>

                        <button
                            class="-mr-1 shrink-0 px-2 py-1 text-gray-400 hover:text-red-500 disabled:opacity-40"
                            :disabled="removingId === brandProduct.id"
                            @click="removeProduct(brandProduct.id)"
                        >
                            <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                        </button>
                    </li>
                </ul>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="flex items-center justify-between gap-x-3 border-b border-gray-900/5 bg-gray-50 px-4 py-3 sm:px-6">
                    <span class="font-semibold text-gray-700">Desain brand</span>
                    <Button
                        size="xs"
                        label="Tambah desain"
                        icon="fal fa-plus"
                        @click="isModalAddDesignOpen = true"
                    />
                </div>
                <div v-if="!designs.length" class="px-6 py-6 text-center text-sm text-gray-400">
                    Belum ada desain
                </div>
                <ul v-else class="divide-y divide-gray-100 text-sm">
                    <li v-for="design in designs" :key="design.id" class="flex items-start justify-between gap-x-3 px-4 py-3 sm:px-6">
                        <div class="min-w-0">
                            <div class="font-medium">{{ design.name }} <span class="italic text-gray-400">{{ design.product || 'Tanpa produk spesifik' }}</span></div>
                            <div class="truncate text-xs text-gray-400">
                                Link: <a :href="design.link" target="_blank" rel="noopener" class="hover:underline">
                                    {{ design.link }}
                                    <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" />
                                </a>
                            </div>
                            <div class="mt-0.5 text-xs text-gray-400">
                                Dibuat: {{ useFormatTime(design.created_at, { formatTime: 'hm' }) }}
                            </div>
                            <div v-if="design.note" class="mt-1 rounded-md bg-gray-100 p-2 text-xs text-gray-600">
                                {{ design.note }}
                            </div>
                        </div>
                        <div class="flex shrink-0 items-center gap-x-3">
                            <Tag v-if="!design.is_used" label="Belum dipakai" :theme="3" noHoverColor />
                        </div>
                    </li>
                </ul>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Pesanan terakhir
                </div>
                <div v-if="!recent_orders.length" class="px-6 py-6 text-center text-sm text-gray-400">
                    Belum ada pesanan
                </div>
                <ul v-else class="divide-y divide-gray-100 text-sm">
                    <li v-for="order in recent_orders" :key="order.slug" class="flex items-center justify-between px-6 py-3">
                        <Link :href="route('kasir.orders.show', order.slug)" class="primaryLink font-mono text-xs">
                            {{ order.reference }}
                        </Link>
                        <div class="flex items-center gap-x-4">
                            <span class="text-xs text-gray-400">{{ order.state_label }}</span>
                            <span class="font-medium">{{ useFormatCurrency(order.total_amount) }}</span>
                        </div>
                    </li>
                </ul>
            </section>
        </div>
    </div>

    <AddBrandDesignModal
        :isOpen="isModalAddDesignOpen"
        :brandId="data.id"
        :products="products"
        :orders="recent_orders"
        @onClose="isModalAddDesignOpen = false"
    />
</template>
